<template>
  <ul
    v-if="safeActions.length"
    class="mt-4 flex flex-wrap gap-2"
    :aria-label="$t('pages.guide.recommendationActionsLabel')"
  >
    <li v-for="action in safeActions" :key="action.id">
      <a
        :href="action.url"
        :target="action.normalizedKind === 'download' ? undefined : '_blank'"
        :rel="
          action.normalizedKind === 'download'
            ? undefined
            : 'noopener noreferrer'
        "
        :download="
          action.normalizedKind === 'download'
            ? action.file_name || true
            : undefined
        "
        :aria-label="actionAriaLabel(action)"
        class="group inline-flex min-h-11 items-center gap-2 rounded-full border border-black bg-white px-4 py-2 text-sm font-bold text-black transition-colors hover:bg-black hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:border-white dark:bg-black dark:text-white dark:hover:bg-white dark:hover:text-black"
        data-test="guide-recommendation-action"
      >
        <Icon
          v-if="action.normalizedKind === 'map'"
          icon="marker-pin"
          class="shrink-0 text-base"
          aria-hidden="true"
        />
        <svg
          v-else-if="action.normalizedKind === 'download'"
          class="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
          <path d="M5 21h14" />
        </svg>
        <svg
          v-else
          class="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M14 5h5v5m0-5-9 9" />
          <path d="M19 13v6H5V5h6" />
        </svg>

        <span>{{ $t(actionLabelKey(action.normalizedKind)) }}</span>
      </a>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import type {
  GuideRecommendationAction,
  GuideRecommendationActionKind,
} from "@/types";
import { getSafeGuideResourceUrl } from "@/utils/guideResources";

type NormalizedAction = GuideRecommendationAction & {
  normalizedKind: GuideRecommendationActionKind;
};

const props = defineProps<{
  actions?: GuideRecommendationAction[];
}>();

const { t } = useI18n();

const safeActions = computed<NormalizedAction[]>(() =>
  (props.actions ?? [])
    .map((action, index) => ({
      ...action,
      url: getSafeGuideResourceUrl(action.url),
      order: action.order ?? index,
      normalizedKind: action.kind === "link" ? ("open" as const) : action.kind,
    }))
    .filter(
      (action): action is NormalizedAction =>
        Boolean(action.url) &&
        ["map", "open", "download"].includes(action.normalizedKind),
    )
    .sort(
      (first, second) =>
        (first.order ?? Number.MAX_SAFE_INTEGER) -
          (second.order ?? Number.MAX_SAFE_INTEGER) ||
        String(first.id).localeCompare(String(second.id)),
    )
    .slice(0, 1),
);

const actionLabelKey = (kind: GuideRecommendationActionKind) => {
  if (kind === "map") return "pages.guide.recommendationActionMap";
  if (kind === "download") return "pages.guide.recommendationActionDownload";
  return "pages.guide.recommendationActionOpen";
};

const actionAriaLabel = (action: NormalizedAction) =>
  [t(actionLabelKey(action.normalizedKind)), action.label]
    .filter(Boolean)
    .join(": ");
</script>
